// DTO → view-model parsers. Every response goes through here (even though the
// wire is already camelCase) so a future server rename breaks only this file
// and consumers never touch a raw DTO.
import {
  OWNER_TYPE,
  isMortgageStatus,
  isPropertyTypeOnDeed,
  isSelectablePropertyType,
  type PropertyTypeOnDeed,
} from '../constants/listingRequest'
import type {
  DocumentExtractionDto,
  ExtractedRentalAgreementDto,
  ExtractedTitleDeedDto,
  ExtractedTitleDeedOwnerDto,
  InitiateSigningDto,
  ListingRequestDetailDto,
  ListingRequestDto,
  ListingRequestEventDto,
  ListingRequestListDto,
  ListingRequestStatusCountsDto,
  SellerPayoutSummaryDto,
  ListingRequestSummaryDto,
  OnboardingFeeQuoteDto,
  PropertyAgreementDto,
  PropertyAgreementSignatoryDto,
} from './listingRequestContracts'
import type {
  DocumentExtraction,
  ExtractedOwner,
  ExtractedRentTerms,
  ExtractedTitleDeed,
  ListingRequest,
  ListingRequestDetail,
  ListingRequestEvent,
  ListingRequestStatusCounts,
  SellerPayoutSummary,
  OwnerDraft,
  ListingRequestListPage,
  ListingRequestSummary,
  OnboardingFeeQuote,
  PropertyAgreement,
  PropertyAgreementSignatory,
  SigningInitiation,
} from '../types/listingRequestTypes'

export function parseListingRequest(dto: ListingRequestDto): ListingRequest {
  return {
    id: dto.id,
    requestRefCode: dto.requestRefCode ?? null,
    requestStatus: dto.requestStatus,
    propertyName: dto.propertyName ?? null,
  }
}

export function parseListingRequestSummary(dto: ListingRequestSummaryDto): ListingRequestSummary {
  return {
    id: dto.id,
    requestRefCode: dto.requestRefCode ?? null,
    propertyName: dto.propertyName ?? null,
    propertyType: dto.propertyType ?? null,
    netSettlementAmount: dto.netSettlementAmount ?? null,
    buildingName: dto.buildingName ?? null,
    locationArea: dto.locationArea ?? null,
    askingPrice: dto.askingPrice ?? null,
    priceAgreed: dto.priceAgreed ?? null,
    currencyCode: dto.currencyCode,
    requestStatus: dto.requestStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageTotal: dto.stageTotal,
    stageState: dto.stageState,
    actionRequired: dto.actionRequired,
    displayStatus: dto.displayStatus ?? '',
    displayProgressDone: dto.displayProgressDone ?? null,
    displayProgressTotal: dto.displayProgressTotal ?? null,
    fundingDeadlineAt: dto.fundingDeadlineAt ?? null,
    fundingDaysRemaining: dto.fundingDaysRemaining ?? null,
    isFundingWindowLapsed: dto.isFundingWindowLapsed ?? false,
    goLiveAt: dto.goLiveAt ?? null,
    fundingWindowDays: dto.fundingWindowDays ?? null,
    submittedAt: dto.submittedAt ?? null,
    createdAt: dto.createdAt,
  }
}

export function parseListingRequestList(dto: ListingRequestListDto): ListingRequestListPage {
  return {
    rows: dto.items.map(parseListingRequestSummary),
    pagination: {
      page: dto.pagination.page,
      limit: dto.pagination.limit,
      totalItems: dto.pagination.totalItems,
      totalPages: dto.pagination.totalPages,
    },
  }
}

// Read the first key that holds a string. The owners snapshot is a loosely-typed
// jsonb blob that seller-service writes in snake_case (`full_name`,
// `mobile_number`, …); the camelCase alias is a fallback so a snapshot written the
// other way still seeds the editor.
function readOwnerString(owner: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = owner[key]
    if (typeof value === 'string') return value
  }
  return ''
}

// The owners snapshot is a jsonb blob the DTO types only as records; this is the
// one place its keys are read, so the store and the detail screen both get typed rows.
function parseOwnerSnapshot(owner: Record<string, unknown>): OwnerDraft {
  const percentage = owner.ownership_percentage ?? owner.ownershipPercentage
  return {
    fullName: readOwnerString(owner, 'full_name', 'fullName'),
    ownershipPercentage: percentage === null || percentage === undefined ? '' : String(percentage),
    email: readOwnerString(owner, 'email'),
    mobileNumber: readOwnerString(owner, 'mobile_number', 'mobileNumber'),
    // Absent on every request submitted before the portal collected it, so a legacy
    // request being revised seeds blank and the form asks the seller to fill it in.
    emiratesId: readOwnerString(owner, 'emirates_id', 'emiratesId'),
    // Defaults to INDIVIDUAL rather than blank: the column behind it is NOT NULL,
    // and every snapshot written before the portal collected this resolves to
    // INDIVIDUAL server-side anyway. Seeding the same value keeps a revision from
    // silently changing an owner's flow.
    ownerType:
      readOwnerString(owner, 'owner_type', 'ownerType') === OWNER_TYPE.COMPANY
        ? OWNER_TYPE.COMPANY
        : OWNER_TYPE.INDIVIDUAL,
    isPrimary: (owner.is_primary ?? owner.isPrimary) === true,
  }
}

export function parseListingRequestDetail(dto: ListingRequestDetailDto): ListingRequestDetail {
  return {
    id: dto.id,
    requestRefCode: dto.requestRefCode ?? null,
    requestStatus: dto.requestStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageTotal: dto.stageTotal,
    stageState: dto.stageState,
    actionRequired: dto.actionRequired,
    displayStatus: dto.displayStatus ?? '',
    displayProgressDone: dto.displayProgressDone ?? null,
    displayProgressTotal: dto.displayProgressTotal ?? null,
    ownershipRole: dto.ownershipRole ?? null,
    propertyType: dto.propertyType ?? null,
    propertyTypeOnDeed: dto.propertyTypeOnDeed ?? null,
    propertyName: dto.propertyName ?? null,
    certificateNumber: dto.certificateNumber ?? null,
    propertyNumber: dto.propertyNumber ?? null,
    buildingName: dto.buildingName ?? null,
    locationArea: dto.locationArea ?? null,
    addressLine1: dto.addressLine1 ?? null,
    city: dto.city ?? null,
    countryId: dto.countryId ?? null,
    plotNumber: dto.plotNumber ?? null,
    municipalityNumber: dto.municipalityNumber ?? null,
    certificateYear: dto.certificateYear ?? null,
    buildingNumber: dto.buildingNumber ?? null,
    mortgageStatus: dto.mortgageStatus ?? null,
    mortgageeName: dto.mortgageeName ?? null,
    deedIssueDate: dto.deedIssueDate ?? null,
    registrationNumber: dto.registrationNumber ?? null,
    registrationDate: dto.registrationDate ?? null,
    purchasePrice: dto.purchasePrice ?? null,
    totalAreaSqm: dto.totalAreaSqm ?? null,
    builtUpAreaSqm: dto.builtUpAreaSqm ?? null,
    titleDeedFileName: dto.titleDeedFileName ?? null,
    titleDeedFileUrl: dto.titleDeedFileUrl ?? null,
    isRented: dto.isRented ?? false,
    // Defaulted like `isRented`: a request submitted before the portal collected
    // these carries neither, and "absent" is the same declaration as "unticked".
    isFreehold: dto.isFreehold ?? false,
    isResidential: dto.isResidential ?? false,
    isMortgageFree: dto.isMortgageFree ?? false,
    isDldBlockFree: dto.isDldBlockFree ?? false,
    termsAcceptedAt: dto.termsAcceptedAt ?? null,
    rentalAgreementFileName: dto.rentalAgreementFileName ?? null,
    rentalAgreementFileUrl: dto.rentalAgreementFileUrl ?? null,
    tenantName: dto.tenantName ?? null,
    annualRentAmount: dto.annualRentAmount ?? null,
    monthlyRentAmount: dto.monthlyRentAmount ?? null,
    leaseStart: dto.leaseStart ?? null,
    leaseEnd: dto.leaseEnd ?? null,
    owners: (dto.owners ?? []).map(parseOwnerSnapshot),
    ownersProgress: (dto.ownersProgress ?? []).map((progress) => ({
      index: progress.index,
      onboardingStatus: progress.onboardingStatus ?? null,
      signingStatus: progress.signingStatus ?? null,
    })),
    askingPrice: dto.askingPrice ?? null,
    adminProposedPrice: dto.adminProposedPrice ?? null,
    adminProposedPriceNote: dto.adminProposedPriceNote ?? null,
    priceAgreed: dto.priceAgreed ?? null,
    priceNegotiationStatus: dto.priceNegotiationStatus,
    valuationMin: dto.valuationMin ?? null,
    valuationMax: dto.valuationMax ?? null,
    currencyCode: dto.currencyCode,
    sellerNotes: dto.sellerNotes ?? null,
    decisionNotes: dto.decisionNotes ?? null,
    rejectionReason: dto.rejectionReason ?? null,
    revisionNotes: dto.revisionNotes ?? null,
    ownersVerifiedAt: dto.ownersVerifiedAt ?? null,
    submittedAt: dto.submittedAt ?? null,
    reviewedAt: dto.reviewedAt ?? null,
    approvedAt: dto.approvedAt ?? null,
    promotedAt: dto.promotedAt ?? null,
    promotedPropertyId: dto.promotedPropertyId ?? null,
    agreementCompletedAt: dto.agreementCompletedAt ?? null,
    fundingDeadlineAt: dto.fundingDeadlineAt ?? null,
    fundingDaysRemaining: dto.fundingDaysRemaining ?? null,
    isFundingWindowLapsed: dto.isFundingWindowLapsed ?? false,
    goLiveAt: dto.goLiveAt ?? null,
    // Never defaulted to 90: the window is per deal now, and a wrong length on a
    // contractual clock is worse than a heading that omits it.
    fundingWindowDays: dto.fundingWindowDays ?? null,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function parseListingRequestEvent(dto: ListingRequestEventDto): ListingRequestEvent {
  return {
    id: dto.id,
    eventType: dto.eventType,
    actorType: dto.actorType,
    summary: dto.summary ?? null,
    data: dto.data ?? null,
    createdAt: dto.createdAt,
  }
}

export function parseListingRequestTimeline(dtos: ListingRequestEventDto[]): ListingRequestEvent[] {
  return dtos.map(parseListingRequestEvent)
}

function parseSignatory(dto: PropertyAgreementSignatoryDto): PropertyAgreementSignatory {
  return {
    id: dto.id,
    signatoryRole: dto.signatoryRole,
    signerName: dto.signerName,
    signerEmail: dto.signerEmail,
    signingOrder: dto.signingOrder,
    signatureStatus: dto.signatureStatus,
    signedAt: dto.signedAt ?? null,
  }
}

export function parseAgreement(dto: PropertyAgreementDto): PropertyAgreement {
  return {
    id: dto.id,
    agreementType: dto.agreementType,
    status: dto.status,
    attemptNo: dto.attemptNo,
    version: dto.version ?? 1,
    supersedesAgreementId: dto.supersedesAgreementId ?? null,
    amendmentDescription: dto.amendmentDescription ?? null,
    signatoryCount: dto.signatoryCount,
    signedCount: dto.signedCount,
    allSigned: dto.allSigned,
    unsignedFilePath: dto.unsignedFilePath ?? null,
    signedFilePath: dto.signedFilePath ?? null,
    generatedAt: dto.generatedAt ?? null,
    sentAt: dto.sentAt ?? null,
    signedAt: dto.signedAt ?? null,
    expiresAt: dto.expiresAt ?? null,
    signatories: dto.signatories.map(parseSignatory),
  }
}

export function parseAgreements(dtos: PropertyAgreementDto[]): PropertyAgreement[] {
  return dtos.map(parseAgreement)
}

export function parseSigningInitiation(dto: InitiateSigningDto): SigningInitiation {
  return {
    signUrl: dto.signUrl,
    agreementId: dto.agreementId,
    signatoryId: dto.signatoryId,
  }
}

// ── Document extraction ───────────────────────────────────────────────────
// Nulls become '' so the values drop straight into form inputs; the wizard skips
// empty values when applying them, so an unread field never blanks a typed value.

function orEmpty(value: string | null): string {
  return value ?? ''
}

function parseExtractedOwner(dto: ExtractedTitleDeedOwnerDto): ExtractedOwner {
  return {
    fullName: orEmpty(dto.fullName),
    ownershipPercentage: dto.ownershipPercentage === null ? '' : String(dto.ownershipPercentage),
    emiratesId: orEmpty(dto.emiratesId),
  }
}

function toPropertyTypeOnDeed(value: string | null | undefined): PropertyTypeOnDeed | null {
  const deedType = value ?? null
  return isPropertyTypeOnDeed(deedType) ? deedType : null
}

// `propertyType` is narrowed to the selectable set (unit / villa): a deed that
// reads as another type seeds nothing, and the seller picks one on Verify.
// `propertyTypeOnDeed` is narrowed to the known deed types the same way.
// `mortgageStatus` is narrowed the same way — only the two known values reach a
// select, and anything else leaves the field as the seller last set it.
function parseExtractedTitleDeed(dto: ExtractedTitleDeedDto | null): ExtractedTitleDeed | null {
  if (!dto) return null
  return {
    certificateNumber: orEmpty(dto.certificateNumber),
    certificateYear: orEmpty(dto.certificateYear),
    propertyName: orEmpty(dto.suggestedPropertyName),
    propertyType: isSelectablePropertyType(dto.propertyType) ? dto.propertyType : null,
    propertyTypeOnDeed: toPropertyTypeOnDeed(dto.propertyTypeOnDeed),
    totalAreaSqm: orEmpty(dto.totalAreaSqm),
    builtUpAreaSqm: orEmpty(dto.builtUpAreaSqm ?? null),
    propertyNumber: orEmpty(dto.propertyNumber),
    buildingName: orEmpty(dto.buildingName),
    locationArea: orEmpty(dto.locationArea),
    addressLine1: orEmpty(dto.addressLine1),
    city: orEmpty(dto.city),
    plotNumber: orEmpty(dto.plotNumber),
    municipalityNumber: orEmpty(dto.municipalityNumber),
    buildingNumber: orEmpty(dto.buildingNumber),
    mortgageStatus: isMortgageStatus(dto.mortgageStatus) ? dto.mortgageStatus : null,
    mortgageeName: orEmpty(dto.mortgageeName),
    deedIssueDate: orEmpty(dto.deedIssueDate),
    registrationNumber: orEmpty(dto.registrationNumber),
    registrationDate: orEmpty(dto.registrationDate),
    purchasePrice: orEmpty(dto.purchasePrice),
    owners: dto.owners.map(parseExtractedOwner),
  }
}

function parseExtractedRentTerms(
  dto: ExtractedRentalAgreementDto | null,
): ExtractedRentTerms | null {
  if (!dto) return null
  return {
    tenantName: orEmpty(dto.tenantName),
    annualRentAmount: orEmpty(dto.annualRentAmount),
    leaseStart: orEmpty(dto.leaseStart),
    leaseEnd: orEmpty(dto.leaseEnd),
  }
}

export function parseDocumentExtraction(dto: DocumentExtractionDto): DocumentExtraction {
  return {
    titleDeed: parseExtractedTitleDeed(dto.titleDeed),
    rentTerms: parseExtractedRentTerms(dto.rentalAgreement),
  }
}

/**
 * The quote is dropped to what the panel renders: the label, how the fee is
 * quoted, and the three amounts. The definition columns the server also sends
 * (`valueType`, `valueBasis`, `paidToRole`, the unit fields) describe how the
 * figure was arrived at and have no place on screen — the seller is told what
 * they are charged, not how the fee model is shaped.
 */
export function parseOnboardingFeeQuote(dto: OnboardingFeeQuoteDto): OnboardingFeeQuote {
  return {
    askingPrice: dto.askingPrice,
    lines: dto.lines.map((line) => ({
      code: line.code,
      label: line.label,
      description: line.description ?? null,
      rateLabel: line.rateLabel ?? null,
      amount: line.amount,
      vatPercentage: line.vatPercentage === null ? null : Number(line.vatPercentage),
      vatAmount: line.vatAmount,
      totalAmount: line.totalAmount,
    })),
    totalFeeAmount: dto.totalFeeAmount,
    totalVatAmount: dto.totalVatAmount,
    totalDeductionAmount: dto.totalDeductionAmount,
    netSettlementAmount: dto.netSettlementAmount,
    isNetNegative: dto.isNetNegative,
  }
}

// Re-keyed from the wire's camelCase fields to the raw `requestStatus` values,
// so the KPI cards can sum by the same status strings the list filter sends.
export function parseListingRequestStatusCounts(
  dto: ListingRequestStatusCountsDto,
): ListingRequestStatusCounts {
  return {
    total: dto.all,
    byStatus: {
      draft: dto.draft,
      submitted: dto.submitted,
      under_review: dto.underReview,
      revision_requested: dto.revisionRequested,
      rejected: dto.rejected,
      approved: dto.approved,
      owner_verification: dto.ownerVerification,
      agreement_in_progress: dto.agreementInProgress,
      agreement_rejected: dto.agreementRejected,
      agreement_completed: dto.agreementCompleted,
      owners_onboarding: dto.ownersOnboarding,
      promoted: dto.promoted,
      withdrawn: dto.withdrawn,
    },
  }
}

// A malformed amount reads as 0 rather than NaN: a tile reading "AED NaN" is
// worse than one reading "AED 0" on a money surface.
function toAmount(value: string | null | undefined): number {
  const amount = Number(value ?? 0)
  return Number.isFinite(amount) ? amount : 0
}

export function parseSellerPayoutSummary(dto: SellerPayoutSummaryDto): SellerPayoutSummary {
  return {
    currencyCode: dto.currencyCode,
    expectedPayout: toAmount(dto.expectedPayout),
    pricedRequestCount: dto.pricedRequestCount,
    paidOut: toAmount(dto.paidOut),
  }
}
